import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Pencil, Trash2, BookOpen, Upload, Download, FileDown } from "lucide-react";
import { toast } from "sonner";
import { getCustomers, addCustomer, updateCustomer, deleteCustomer, addCustomersBulk, deleteCustomersBulk, getBills } from "@/lib/api";
import { exportCSV } from "@/lib/exportUtils";
import FilterBar, { useSessionFilters } from "@/components/FilterBar";

const emptyForm = {
  name: "", society: "", plotNo: "", flatNo: "", towerNo: "",
  phone: "", consumerNumber: "", connectionType: "PNG", lastReading: 0, initialReading: 0,
};

const CUST_FILTER_DEFAULTS = {
  society: "all", tower: "all", connectionType: "all",
  status: "all", pendingDues: "all", lastBilled: "all",
};

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvRows, setCsvRows] = useState([]);
  const [csvError, setCsvError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [pendingForm, setPendingForm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [allBills, setAllBills] = useState([]);
  const [filters, setFilters] = useSessionFilters("customers", CUST_FILTER_DEFAULTS);

  useEffect(() => {
    getCustomers().then(setCustomers).catch((e) => { console.warn("Failed to load customers:", e.message); });
    getBills().then(setAllBills).catch((e) => { console.warn("Failed to load bills:", e.message); });
  }, []);

  const getDue = useCallback((cid) => allBills.filter((b) => b.customerId === cid && !b.isPaid).reduce((s, b) => s + (b.netPayable || 0), 0), [allBills]);

  const societies = useMemo(() => Array.from(new Set(customers.map((c) => c.society).filter(Boolean))).sort(), [customers]);
  const towers = useMemo(() => {
    const src = filters.society !== "all" ? customers.filter((c) => c.society === filters.society) : customers;
    return Array.from(new Set(src.map((c) => c.towerNo).filter(Boolean))).sort();
  }, [customers, filters.society]);

  const filterConfig = useMemo(() => [
    { key: "society", label: "Society", type: "select", options: [{ value: "all", label: "All Societies" }, ...societies.map((s) => ({ value: s, label: s }))] },
    { key: "tower", label: "Tower", type: "select", options: [{ value: "all", label: "All Towers" }, ...towers.map((t) => ({ value: t, label: t }))] },
    { key: "connectionType", label: "Connection", type: "select", options: [{ value: "all", label: "All Types" }, { value: "PNG", label: "PNG" }, { value: "LPG", label: "LPG" }] },
    { key: "pendingDues", label: "Pending Dues", type: "select", options: [{ value: "all", label: "All" }, { value: "yes", label: "Has Dues" }, { value: "no", label: "No Dues" }] },
    { key: "lastBilled", label: "Last Billed", type: "select", options: [{ value: "all", label: "All" }, { value: "this_month", label: "This Month" }, { value: "last_month", label: "Last Month" }, { value: "never", label: "Never Billed" }] },
  ], [societies, towers]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = `${lm.getFullYear()}-${String(lm.getMonth() + 1).padStart(2, "0")}`;
    return customers.filter((c) => {
      if (q && !(c.name?.toLowerCase().includes(q) || c.flatNo?.toLowerCase().includes(q) || c.consumerNumber?.toLowerCase().includes(q) || c.society?.toLowerCase().includes(q))) return false;
      if (filters.society !== "all" && c.society !== filters.society) return false;
      if (filters.tower !== "all" && c.towerNo !== filters.tower) return false;
      if (filters.connectionType !== "all" && c.connectionType !== filters.connectionType) return false;
      if (filters.pendingDues !== "all") {
        const due = getDue(c.id);
        if (filters.pendingDues === "yes" && due <= 0) return false;
        if (filters.pendingDues === "no" && due > 0) return false;
      }
      if (filters.lastBilled !== "all") {
        const custBills = allBills.filter((b) => b.customerId === c.id);
        if (filters.lastBilled === "never" && custBills.length > 0) return false;
        if (filters.lastBilled === "this_month") {
          if (!custBills.some((b) => b.billDate?.startsWith(thisMonth))) return false;
        }
        if (filters.lastBilled === "last_month") {
          if (!custBills.some((b) => b.billDate?.startsWith(lastMonth))) return false;
        }
      }
      return true;
    });
  }, [customers, search, filters, allBills, getDue]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "", society: c.society || "", plotNo: c.plotNo || "",
      flatNo: c.flatNo || "", towerNo: c.towerNo || "", phone: c.phone || "",
      consumerNumber: c.consumerNumber || "", connectionType: c.connectionType || "PNG",
      lastReading: c.lastReading || 0, initialReading: c.initialReading || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.consumerNumber.trim()) { toast.error("Consumer number is required"); return; }
    if (!editingId) {
      const dup = customers.find((c) => c.consumerNumber?.toLowerCase() === form.consumerNumber.trim().toLowerCase());
      if (dup) { setDuplicateWarning(dup); setPendingForm({ ...form }); return; }
    }
    await doSave(form);
  };

  const doSave = async (data) => {
    if (editingId) {
      await updateCustomer(editingId, data);
      toast.success("Customer updated");
    } else {
      await addCustomer(data);
      toast.success("Customer added");
    }
    setCustomers(await getCustomers());
    setDialogOpen(false);
    setDuplicateWarning(null);
    setPendingForm(null);
  };

  const handleDuplicateConfirm = async () => {
    if (pendingForm) await doSave(pendingForm);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteCustomer(deleteId);
      setCustomers(await getCustomers());
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(deleteId); return n; });
      toast.success("Customer deleted");
      setDeleteId(null);
    }
  };

  const toggleId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.map((c) => c.id)));
    else setSelectedIds(new Set());
  };

  const allSelected = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    await deleteCustomersBulk(ids);
    setCustomers(await getCustomers());
    setSelectedIds(new Set());
    setBulkDeleteOpen(false);
    toast.success(`${ids.length} customer${ids.length !== 1 ? "s" : ""} deleted`);
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) { setCsvError("CSV must have a header row and at least one data row"); return; }

        // Proper CSV parser that handles quoted fields with commas
        const parseLine = (line) => {
          const result = [];
          let current = "";
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { inQuotes = !inQuotes; }
            else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ""; }
            else { current += ch; }
          }
          result.push(current.trim());
          return result;
        };

        const headers = parseLine(lines[0]).map((h) => h.toLowerCase());
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const vals = parseLine(lines[i]);
          const row = {};
          headers.forEach((h, idx) => { row[h] = vals[idx] || ""; });
          rows.push({
            name: row["name"] || row["customer name"] || "",
            consumerNumber: row["consumer number"] || row["consumer no"] || row["consumernumber"] || "",
            society: row["society"] || "",
            flatNo: row["flat no"] || row["flatno"] || row["flat"] || "",
            plotNo: row["plot no"] || row["plotno"] || row["plot"] || "",
            towerNo: row["tower no"] || row["towerno"] || row["tower"] || "",
            phone: row["phone"] || row["mobile"] || "",
            connectionType: (row["connection type"] || row["type"] || "PNG").toUpperCase(),
            lastReading: parseFloat(row["initial reading"] || row["reading"] || "0") || 0,
            initialReading: parseFloat(row["initial reading"] || row["reading"] || "0") || 0,
          });
        }
        setCsvRows(rows.filter((r) => r.name && r.consumerNumber));
        setCsvError(rows.length === 0 ? "No valid rows found" : "");
        setCsvDialogOpen(true);
      } catch { setCsvError("Failed to parse CSV file"); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleCsvImport = async () => {
    let count = csvRows.length;
    await addCustomersBulk(csvRows);
    setCustomers(await getCustomers());
    setCsvDialogOpen(false);
    setCsvRows([]);
    toast.success(`${count} customer${count !== 1 ? "s" : ""} imported successfully`);
  };

  const downloadSampleCsv = () => {
    const csv = "Name,Consumer Number,Society,Flat No,Plot No,Tower No,Phone,Connection Type,Initial Reading\nRajesh Kumar,CN-001,Sunrise Towers,A-101,,T1,9876543210,PNG,0\nPriya Sharma,CN-002,Green Valley,B-202,,,9876543211,PNG,0";
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "customer_import_template.csv";
    link.click();
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">
          Customers
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="export-customers-csv" variant="outline" size="sm" onClick={() => exportCSV(filtered, [
            { label: "Name", accessor: (r) => r.name },
            { label: "Consumer No", accessor: (r) => r.consumerNumber },
            { label: "Society", accessor: (r) => r.society },
            { label: "Flat No", accessor: (r) => r.flatNo },
            { label: "Phone", accessor: (r) => r.phone },
            { label: "Type", accessor: (r) => r.connectionType },
            { label: "Last Reading", accessor: (r) => (r.lastReading || 0).toFixed(2) },
            { label: "Total Due", accessor: (r) => getDue(r.id).toFixed(2) },
          ], "customers")} className="rounded-sm border-zinc-300 text-xs">
            <FileDown className="w-3 h-3 mr-1" /> Export CSV
          </Button>
          <Button
            data-testid="download-sample-csv-button"
            variant="outline"
            onClick={downloadSampleCsv}
            className="rounded-sm border-zinc-300 text-sm"
          >
            <Download className="w-4 h-4 mr-2" /> CSV Template
          </Button>
          <label
            data-testid="import-csv-button"
            className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 rounded-sm text-sm cursor-pointer hover:bg-zinc-50 transition-colors font-medium"
          >
            <Upload className="w-4 h-4" /> Import CSV
            <input type="file" accept=".csv" onChange={handleCsvFile} className="hidden" />
          </label>
          <Button
            data-testid="add-customer-button"
            onClick={openAdd}
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Customer
          </Button>
        </div>
      </div>

      <FilterBar
        pageKey="customers"
        defaults={CUST_FILTER_DEFAULTS}
        config={filterConfig}
        filters={filters}
        setFilters={setFilters}
        filteredCount={filtered.length}
        totalCount={customers.length}
      />

      <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
        <CardContent className="p-0">
          <div className="p-4 border-b border-zinc-200 flex flex-wrap items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                data-testid="customer-search-input"
                placeholder="Search by name, flat, consumer no..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-zinc-50 border-zinc-300 rounded-none"
              />
            </div>
            {selectedIds.size > 0 && (
              <Button
                data-testid="bulk-delete-button"
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteOpen(true)}
                className="rounded-sm"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.size} Selected
              </Button>
            )}
            <span className="text-xs text-zinc-400 font-mono">{filtered.length} customer{filtered.length !== 1 ? "s" : ""}</span>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-zinc-50">
                  <TableHead className="w-10">
                    <Checkbox data-testid="select-all-customers" checked={allSelected} onCheckedChange={toggleAll} />
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Name</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Consumer No</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Society</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Flat/Plot</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Phone</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Type</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Last Reading</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right">Total Due</TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 text-right sticky right-0 bg-zinc-50">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-sm text-zinc-400 py-12">
                      {search ? "No customers match your search." : "No customers yet. Add your first customer."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((c) => (
                    <TableRow key={c.id} data-testid={`customer-row-${c.id}`} className={`hover:bg-zinc-50 ${selectedIds.has(c.id) ? "bg-orange-50/50" : ""}`}>
                      <TableCell>
                        <Checkbox data-testid={`select-customer-${c.id}`} checked={selectedIds.has(c.id)} onCheckedChange={() => toggleId(c.id)} />
                      </TableCell>
                      <TableCell className="font-medium text-sm">{c.name}</TableCell>
                      <TableCell className="text-sm font-mono">{c.consumerNumber}</TableCell>
                      <TableCell className="text-sm">{c.society}</TableCell>
                      <TableCell className="text-sm">{c.flatNo || c.plotNo}</TableCell>
                      <TableCell className="text-sm font-mono">{c.phone || "—"}</TableCell>
                      <TableCell className="text-sm">{c.connectionType}</TableCell>
                      <TableCell className="text-sm font-mono">{(c.lastReading || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-sm font-mono text-right">{(() => { const d = getDue(c.id); return d > 0 ? <span className="text-red-600 font-semibold">₹{d.toFixed(2)}</span> : <span className="text-emerald-600">₹0.00</span>; })()}</TableCell>
                      <TableCell className="text-right sticky right-0 bg-white">
                        <div className="flex items-center justify-end gap-1">
                          <Button data-testid={`ledger-customer-${c.id}`} variant="ghost" size="sm" onClick={() => navigate(`/customers/${c.id}/ledger`)} title="View Ledger">
                            <BookOpen className="w-4 h-4" />
                          </Button>
                          <Button data-testid={`edit-customer-${c.id}`} variant="ghost" size="sm" onClick={() => openEdit(c)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-customer-${c.id}`}
                            variant="ghost" size="sm"
                            onClick={() => setDeleteId(c.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">
              {editingId ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
            <DialogDescription>
              {editingId ? "Update customer details below." : "Fill in the customer details below."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Customer Name *</Label>
              <Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Consumer Number *</Label>
              <Input data-testid="customer-consumer-number-input" value={form.consumerNumber} onChange={(e) => setForm({ ...form, consumerNumber: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Society</Label>
              <Input data-testid="customer-society-input" value={form.society} onChange={(e) => setForm({ ...form, society: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Plot No</Label>
              <Input data-testid="customer-plot-input" value={form.plotNo} onChange={(e) => setForm({ ...form, plotNo: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Flat No</Label>
              <Input data-testid="customer-flat-input" value={form.flatNo} onChange={(e) => setForm({ ...form, flatNo: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Tower No</Label>
              <Input data-testid="customer-tower-input" value={form.towerNo} onChange={(e) => setForm({ ...form, towerNo: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
            </div>
            <div>
              <Label>Connection Type</Label>
              <Select value={form.connectionType} onValueChange={(v) => setForm({ ...form, connectionType: v })}>
                <SelectTrigger data-testid="customer-connection-type-select" className="bg-zinc-50 border-zinc-300 rounded-none mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PNG">PNG</SelectItem>
                  <SelectItem value="LPG">LPG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!editingId && (
              <div className="col-span-2">
                <Label>Initial Meter Reading</Label>
                <Input data-testid="customer-initial-reading-input" type="number" step="0.01" value={form.lastReading} onChange={(e) => { const v = parseFloat(e.target.value) || 0; setForm({ ...form, lastReading: v, initialReading: v }); }} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
              </div>
            )}
            {editingId && (
              <div className="col-span-2">
                <Label>Initial Reading (opening meter reading)</Label>
                <Input data-testid="customer-initial-reading-edit" type="number" step="0.01" value={form.initialReading} onChange={(e) => setForm({ ...form, initialReading: parseFloat(e.target.value) || 0 })} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="save-customer-button" onClick={handleSave} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
              {editingId ? "Update" : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this customer and all their bills. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Customer{selectedIds.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} selected customer{selectedIds.size !== 1 ? "s" : ""} and all their associated bills. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk-delete" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-delete" onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-sm">
              Delete {selectedIds.size} Customer{selectedIds.size !== 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Preview Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Import Customers from CSV</DialogTitle>
            <DialogDescription>
              {csvRows.length} customer{csvRows.length !== 1 ? "s" : ""} found. Review and confirm import.
            </DialogDescription>
          </DialogHeader>
          {csvError && <p className="text-sm text-red-600">{csvError}</p>}
          <div className="flex gap-2 mb-2">
            <Button data-testid="download-sample-csv" variant="outline" size="sm" onClick={downloadSampleCsv} className="rounded-sm text-xs">
              <Download className="w-3 h-3 mr-1" /> Download Sample CSV
            </Button>
          </div>
          {csvRows.length > 0 && (
            <div className="overflow-x-auto border border-zinc-200 max-h-[40vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="text-xs font-bold uppercase">#</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Name</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Consumer No</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Society</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Flat/Plot</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Phone</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvRows.map((row, i) => (
                    <TableRow key={`csv-${row.consumerNumber}-${i}`}>
                      <TableCell className="text-xs text-zinc-400">{i + 1}</TableCell>
                      <TableCell className="text-sm">{row.name}</TableCell>
                      <TableCell className="text-sm font-mono">{row.consumerNumber}</TableCell>
                      <TableCell className="text-sm">{row.society}</TableCell>
                      <TableCell className="text-sm">{row.flatNo || row.plotNo}</TableCell>
                      <TableCell className="text-sm font-mono">{row.phone}</TableCell>
                      <TableCell className="text-sm">{row.connectionType}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="confirm-csv-import" onClick={handleCsvImport} disabled={csvRows.length === 0} className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
              Import {csvRows.length} Customer{csvRows.length !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Customer Warning */}
      <AlertDialog open={!!duplicateWarning} onOpenChange={() => { setDuplicateWarning(null); setPendingForm(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Consumer Number</AlertDialogTitle>
            <AlertDialogDescription>
              A customer with consumer number <span className="font-mono font-semibold">{duplicateWarning?.consumerNumber}</span> already exists:
              <span className="block mt-2 p-2 bg-zinc-100 border border-zinc-200 text-sm">
                <span className="font-semibold">{duplicateWarning?.name}</span>
                {duplicateWarning?.society && ` — ${duplicateWarning.society}`}
                {duplicateWarning?.flatNo && `, Flat ${duplicateWarning.flatNo}`}
              </span>
              <span className="block mt-2">Do you still want to add this customer?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-duplicate" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-duplicate"
              onClick={handleDuplicateConfirm}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm"
            >
              Add Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
